import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, CircleCheck, FileText } from "lucide-react";
import { useStore } from "../lib/store";
import { isDemoMode } from "../lib/supabase";
import { showFeedback } from "../lib/feedback";
import { readPreference } from "../features/preferences/contracts";
import {
  REPORT_MODELS,
  REPORT_PERIODS,
  buildReportSnapshot,
  isReportPeriod,
  reportScopeNote,
  reportSections,
  type ReportModel,
  type ReportPeriod,
} from "../features/reports/snapshot";
import { drawReportPages } from "../features/reports/draw";
import { dataUrlBytes, jpegPagesToPdf } from "../features/reports/pdf";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Field } from "../ui/Field";
import { Select } from "../ui/Select";
import "./export.css";

/**
 * Relatório de prospecção, no formato do protótipo: modelo e período à
 * esquerda, a folha do PDF à direita com paginação. A prévia e o arquivo são
 * as mesmas páginas — o botão só codifica o que já está desenhado.
 */
export function ReportDialog({
  onClose,
  period: initialPeriod,
  funnelId,
}: {
  onClose: () => void;
  /** Período da tela de origem; fora dos quatro do relatório, abre em 30 dias. */
  period?: string | null;
  funnelId?: string;
}) {
  const store = useStore();
  const [model, setModel] = useState<ReportModel>("simple");
  const [period, setPeriod] = useState<ReportPeriod>(isReportPeriod(initialPeriod) ? initialPeriod : "mes");
  const [pages, setPages] = useState<HTMLCanvasElement[]>([]);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);

  const now = useMemo(() => new Date().toISOString(), []);
  const agency = readPreference(store.userPreferences ?? [], "profile").value.agencyName?.trim() || "CosmosLeads";
  const openNotices = useMemo(() => store.noticeFeed().length, [store]);

  const snapshot = useMemo(
    () =>
      buildReportSnapshot({
        model,
        period,
        now,
        funnelId,
        agency,
        demo: isDemoMode,
        leads: store.leads,
        memberships: store.memberships,
        stages: store.stages,
        funnels: store.funnels,
        tags: store.tags,
        tasks: store.tasks,
        events: store.events,
        receipts: store.receipts ?? [],
        revenueGoals: store.revenueGoals ?? [],
        openNotices,
      }),
    [model, period, now, funnelId, agency, store, openNotices],
  );

  // As páginas esperam as fontes: desenhar antes delas sairia em fonte do
  // sistema na prévia e no arquivo. Uma troca de modelo no meio descarta a
  // montagem antiga.
  useEffect(() => {
    let current = true;
    setStatus("loading");
    setPages([]);
    setPage(0);
    Promise.resolve(document.fonts?.ready)
      .then(() => {
        if (!current) return;
        setPages(drawReportPages(snapshot));
        setStatus("ready");
      })
      .catch(() => {
        if (current) setStatus("error");
      });
    return () => {
      current = false;
    };
  }, [snapshot, attempt]);

  useEffect(() => {
    const paper = paperRef.current;
    const canvas = pages[page];
    if (!paper || !canvas) return;
    canvas.setAttribute("role", "img");
    canvas.setAttribute(
      "aria-label",
      `Prévia da página ${page + 1} do relatório ${model === "full" ? "completo" : "simples"}. O conteúdo incluído está descrito ao lado.`,
    );
    paper.replaceChildren(canvas);
  }, [pages, page, model, status]);

  const download = async () => {
    if (status !== "ready" || !pages.length) return;
    setBusy(true);
    try {
      const images = pages.map((canvas) => ({
        width: canvas.width,
        height: canvas.height,
        bytes: dataUrlBytes(canvas.toDataURL("image/jpeg", 0.94)),
      }));
      const bytes = jpegPagesToPdf(images);
      if (typeof URL.createObjectURL !== "function") throw new Error("sem download");
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `cosmos-relatorio-${model === "full" ? "completo" : "simples"}-${period}.pdf`;
      anchor.click();
      // O navegador precisa do endereço até começar a baixar; depois ele é
      // revogado (AGENTS §26.3).
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      showFeedback("PDF preparado para download.", "success");
      onClose();
    } catch {
      showFeedback("Não foi possível gerar o PDF. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const footnote =
    status === "ready"
      ? `PDF · ${pages.length} ${pages.length === 1 ? "página" : "páginas"}`
      : status === "error"
        ? "Prévia indisponível"
        : "PDF · preparando prévia";
  const hint = REPORT_PERIODS.find((item) => item.id === period)?.hint ?? "";

  return (
    <Dialog
      open
      onClose={onClose}
      width="wide"
      className="cx-transfer cx-transfer-report"
      icon={<FileText size={21} />}
      title="Relatório de prospecção"
      description="Escolha o modelo e visualize o PDF antes de baixar."
      footer={
        <>
          <span className="cx-transfer-footnote" aria-live="polite">
            {footnote}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void download()} disabled={status !== "ready"} loading={busy}>
            {busy ? "Preparando PDF…" : "Baixar PDF"}
          </Button>
        </>
      }
    >
      <div className="cx-transfer-layout">
        <section className="cx-transfer-config" aria-label="Configurar relatório">
          <h3 className="cx-transfer-models-title">Modelo do relatório</h3>
          <div className="cx-transfer-models" role="group" aria-label="Modelo do relatório">
            {REPORT_MODELS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="cx-transfer-choice"
                aria-pressed={model === item.id}
                onClick={() => setModel(item.id)}
              >
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.detail}</small>
                </span>
                <span className="cx-transfer-check" aria-hidden="true">
                  <Check />
                </span>
              </button>
            ))}
          </div>
          <Field label="Período">
            {({ id }) => (
              <Select
                id={id}
                label="Período"
                size="field"
                value={period}
                onChange={(value) => setPeriod(value)}
                options={REPORT_PERIODS.map((item) => ({ value: item.id, label: item.label }))}
              />
            )}
          </Field>
          <fieldset>
            <legend>Conteúdo incluído</legend>
            <ul className="cx-transfer-sections">
              {reportSections(model).map((item) => (
                <li key={item}>
                  <CircleCheck size={15} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </fieldset>
          <p className="cx-transfer-note">{reportScopeNote(model)}</p>
        </section>

        <section className="cx-transfer-preview" aria-label="Prévia do PDF">
          <div className="cx-transfer-preview-head">
            <h3>Prévia do relatório</h3>
            <span className="cx-transfer-badge is-pdf">PDF</span>
          </div>
          {status === "error" ? (
            <>
              <div className="cx-transfer-empty">
                <FileText size={24} aria-hidden="true" />
                <strong>Não foi possível montar a prévia</strong>
                <p>Tente carregar novamente. Suas escolhas foram mantidas.</p>
              </div>
              <Button variant="ghost" className="cx-transfer-retry" onClick={() => setAttempt((value) => value + 1)}>
                Tentar novamente
              </Button>
            </>
          ) : (
            <>
              <p className="cx-transfer-report-hint">
                {hint} · {isDemoMode ? "dados demonstrativos" : "dados do período"}
              </p>
              <div className="cx-transfer-paper">
                {status === "loading" && (
                  <p className="cx-transfer-loading" role="status">
                    Preparando prévia…
                  </p>
                )}
                {/* A folha é um canvas já desenhado; o React não tem filhos
                    aqui, então trocar a página não disputa o nó com ele. */}
                <div className="cx-transfer-sheet" ref={paperRef} hidden={status !== "ready"} />
              </div>
              {status === "ready" && (
                <nav className="cx-transfer-pager" aria-label="Páginas do PDF">
                  <IconButton
                    label="Página anterior"
                    icon={<ChevronLeft size={15} />}
                    disabled={page === 0}
                    onClick={() => setPage((value) => Math.max(0, value - 1))}
                  />
                  <span aria-live="polite">
                    Página {page + 1} de {pages.length}
                  </span>
                  <IconButton
                    label="Próxima página"
                    icon={<ChevronRight size={15} />}
                    disabled={page >= pages.length - 1}
                    onClick={() => setPage((value) => Math.min(pages.length - 1, value + 1))}
                  />
                </nav>
              )}
            </>
          )}
        </section>
      </div>
    </Dialog>
  );
}
